import { __rest } from './../../external/tslib/tslib.es6.js';
import React, { useRef, useState, useEffect } from 'react';
import { Container, Wrapper, Overline, Title, CloseIcon, ContentString, ContentElement } from './styles.js';
import { debounce, hasKeyInDirection, getInvertedTopOrBottomDirection } from './utils.js';
import { Flex } from '../Flex/Flex.js';

function Popover(_a) {
    var { active = false, children, content, delay = 300, disableClickAway = false, hasCloseButton = false, onClose, onOpen, placement = 'bottom', title, overline, trigger = 'click', variation = 'neutral', disabled = false } = _a, props = __rest(_a, ["active", "children", "content", "delay", "disableClickAway", "hasCloseButton", "onClose", "onOpen", "placement", "title", "overline", "trigger", "variation", "disabled"]);
    const refPopover = useRef(null);
    const refContainer = useRef(null);
    const [isOpen, setIsOpen] = useState(active);
    const [placementInverter, setPlacementInverter] = useState(null);
    const shouldDisableClickAway = active || disableClickAway;
    const debouncedOpen = debounce(() => {
        if (disabled)
            return;
        setIsOpen(true);
        if (onOpen)
            onOpen();
    }, delay);
    const debouncedClose = debounce(() => {
        setIsOpen(false);
        if (onClose)
            onClose();
    }, delay);
    const handleHoverOpen = () => {
        debouncedClose.cancel();
        debouncedOpen();
    };
    const handleHoverClose = () => {
        debouncedOpen.cancel();
        debouncedClose();
    };
    const handleImmediateClose = () => {
        debouncedOpen.cancel();
        debouncedClose.cancel();
        if (isOpen) {
            setIsOpen(false);
            if (onClose)
                onClose();
        }
    };
    const handleClickAway = ({ target }) => {
        const shouldClose = (target) => isOpen && refContainer.current && !refContainer.current.contains(target);
        if (shouldClose(target)) {
            handleImmediateClose();
        }
    };
    const handleTrigger = () => {
        switch (trigger) {
            case 'hover':
                return {
                    onMouseEnter: handleHoverOpen,
                    onMouseLeave: handleHoverClose,
                };
            case 'click':
            default:
                return {
                    onClick: () => {
                        const newIsOpen = !isOpen;
                        if (disabled && newIsOpen)
                            return;
                        setIsOpen(newIsOpen);
                        if (newIsOpen && onOpen)
                            onOpen();
                        if (!newIsOpen && onClose)
                            onClose();
                    },
                };
        }
    };
    const getPlacementInverted = () => {
        const hasDirectionInvert = hasKeyInDirection(placement);
        if (hasDirectionInvert && isOpen && !placementInverter) {
            setTimeout(() => {
                var _a;
                const elementMeasures = (_a = refPopover === null || refPopover === void 0 ? void 0 : refPopover.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                const newPlacementInverter = getInvertedTopOrBottomDirection(elementMeasures, placement);
                if (newPlacementInverter !== placementInverter) {
                    setPlacementInverter(getInvertedTopOrBottomDirection(elementMeasures, placement));
                }
            }, 0);
        }
        if (hasDirectionInvert && !isOpen && placementInverter) {
            setPlacementInverter(null);
        }
    };
    useEffect(() => {
        getPlacementInverted();
        if (!shouldDisableClickAway && isOpen) {
            document.addEventListener('click', handleClickAway);
        }
        else {
            document.removeEventListener('click', handleClickAway);
        }
        return () => {
            document.removeEventListener('click', handleClickAway);
        };
    }, [isOpen]);
    useEffect(() => {
        return () => {
            debouncedOpen.cancel();
            debouncedClose.cancel();
        };
    }, []);
    const isContentAlone = !overline && !title;
    const handleContent = (contentData) => {
        if (typeof contentData === 'string') {
            return React.createElement(ContentString, null, contentData);
        }
        return React.createElement(ContentElement, null, contentData);
    };
    return (React.createElement(Container, Object.assign({ ref: refContainer }, handleTrigger()),
        React.createElement(Wrapper, Object.assign({}, props, { placement: placementInverter || placement, isOpen: isOpen, ref: refPopover, variation: variation }),
            React.createElement(Flex, { justifyContent: "space-between", w: "100%" },
                React.createElement("div", null,
                    overline && React.createElement(Overline, null, overline),
                    title && React.createElement(Title, null, title),
                    isContentAlone ? handleContent(content) : null),
                trigger === 'click' && hasCloseButton && (React.createElement(CloseIcon, { onClick: handleImmediateClose }))),
            !isContentAlone ? handleContent(content) : null),
        children));
}

export { Popover as default };
